import React, { useEffect, useMemo, useRef, useState } from 'react';
import api from '../../services/api';
import { lerPesoBalanca } from '../../utils/balanca';
import s from './ModalMesasComandas.module.css';

/* Botão Pesados do PDV (Parâmetros → PDV → "Habilitar botão Pesados").
   Grade de fotos/preços igual à de Mesas/Comandas. Clique no produto:
   balança ligada → lê o peso e já lança no PDV; desligada (ou sem resposta)
   → pede o peso, Enter lança. Esc/Sair volta pro PDV.
   Atalhos numéricos: em "Todos" o número abre o grupo; dentro do grupo,
   o número escolhe o produto. Com a busca preenchida, dígito é texto. */

const FILTROS = ['pesado_no_caixa', 'produto_balanca', 'marcador'];
const fmtBRL = v => Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const fmtPeso = q => Number(q).toLocaleString('pt-BR', { minimumFractionDigits: 3, maximumFractionDigits: 3 });
// Peso: "1,250" e "1.250" valem 1,25 kg (ninguém pesa milhares de kg no caixa).
const lerNumero = t => parseFloat(String(t || '').replace(',', '.'));

const seloGrupo = {
  display: 'inline-block', minWidth: 18, padding: '0 5px', marginRight: 6, borderRadius: 9,
  background: '#1f2937', color: '#fff', fontSize: 11, lineHeight: '18px', textAlign: 'center',
};
const seloItem = {
  position: 'absolute', top: 4, left: 4, zIndex: 1, minWidth: 22, padding: '0 6px', borderRadius: 11,
  background: '#1565FF', color: '#fff', fontSize: 12, fontWeight: 700, lineHeight: '22px',
  textAlign: 'center', boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
};

export default function ModalPesados({ aberto, filtro, balancaAtiva, onToggleBalanca, precoDoProduto, onAdicionar, onFechar }) {
  const [produtos, setProdutos] = useState([]);
  const [grupos, setGrupos] = useState([]);
  const [carregando, setCarregando] = useState(false);
  const [busca, setBusca] = useState('');
  const [grupoSelId, setGrupoSelId] = useState(null);
  const [lendo, setLendo] = useState(false);
  const [manual, setManual] = useState(null); // { produto, aviso }
  const [pesoDigitado, setPesoDigitado] = useState('');
  const [erro, setErro] = useState('');
  const [lancados, setLancados] = useState([]);
  const [atalho, setAtalho] = useState('');
  const buscaRef = useRef(null);
  const pesoRef = useRef(null);

  const chaveFiltro = FILTROS.includes(filtro) ? filtro : 'pesado_no_caixa';

  useEffect(() => {
    if (!aberto) return;
    setBusca(''); setGrupoSelId(null); setManual(null); setErro(''); setLancados([]); setAtalho('');
    setCarregando(true);
    Promise.all([
      api.get('/produtos', { params: { todos: 'true', [chaveFiltro]: 'true' } }).then(r => r.data || []),
      api.get('/grupos').then(r => r.data || []).catch(() => []),
    ]).then(([ps, gs]) => { setProdutos(ps); setGrupos(gs); })
      .catch(() => setErro('Não foi possível carregar os produtos.'))
      .finally(() => setCarregando(false));
    setTimeout(() => buscaRef.current?.focus(), 50);
  }, [aberto, chaveFiltro]);

  useEffect(() => { if (manual) setTimeout(() => pesoRef.current?.focus(), 30); }, [manual]);

  const gruposComProduto = useMemo(() => {
    const ids = new Set(produtos.map(p => p.grupo_id).filter(Boolean));
    return grupos.filter(g => ids.has(g.id));
  }, [produtos, grupos]);

  const filtrados = useMemo(() => produtos.filter(p => {
    if (grupoSelId && p.grupo_id !== grupoSelId) return false;
    if (!busca) return true;
    const b = busca.toLowerCase();
    return p.nome.toLowerCase().includes(b) || String(p.codigo_interno || '').includes(busca)
      || (p.codigo_barras || '').includes(busca);
  }), [produtos, grupoSelId, busca]);

  // Atalhos numéricos: em "Todos", o número escolhe o grupo (1º botão = 1);
  // com um grupo aberto, escolhe o produto pela posição na grade.
  const numerarItens = !!grupoSelId;
  const totalAtalhos = numerarItens ? filtrados.length : gruposComProduto.length;

  useEffect(() => { setAtalho(''); }, [grupoSelId]);


  const lancar = (produto, peso) => {
    if (!(peso > 0)) { setErro('Peso inválido — informe um peso maior que zero.'); return false; }
    if (!produto.unidade_fraciona && !Number.isInteger(peso)) {
      setErro(`A unidade "${produto.unidade || 'UN'}" de ${produto.nome} não fraciona — informe uma quantidade inteira.`);
      return false;
    }
    const preco = precoDoProduto(produto);
    onAdicionar(produto, peso, preco);
    setErro('');
    setLancados(l => [{ nome: produto.nome, peso, un: produto.unidade || '', total: Math.round(peso * preco * 100) / 100 }, ...l].slice(0, 8));
    return true;
  };

  const clicarProduto = async (produto) => {
    if (lendo) return;
    setErro('');
    if (!balancaAtiva) { setPesoDigitado(''); setManual({ produto }); return; }
    setLendo(true);
    try {
      const peso = Number(await lerPesoBalanca());
      if (!(peso > 0)) {
        setPesoDigitado(''); setManual({ produto, aviso: 'A balança marcou peso zero — coloque o produto ou digite o peso.' });
        return;
      }
      lancar(produto, peso);
    } catch (e) {
      setPesoDigitado('');
      setManual({ produto, aviso: 'Balança não encontrada — digite o peso.', detalhe: e.message });
    } finally {
      setLendo(false);
    }
  };

  const confirmarManual = () => {
    if (!manual) return;
    if (lancar(manual.produto, lerNumero(pesoDigitado))) { setManual(null); setPesoDigitado(''); }
  };

  // Teclado da tela:
  //  - dígitos (com a busca vazia) = atalho; entra na hora quando nenhum número
  //    maior começa com o que foi digitado, senão espera outro dígito ou Enter;
  //  - Esc: limpa o atalho → fecha a digitação do peso → volta pra "Todos" → sai.
  useEffect(() => {
    if (!aberto) return undefined;
    const escolher = n => {
      setAtalho('');
      if (numerarItens) { const p = filtrados[n - 1]; if (p) clicarProduto(p); }
      else { const g = gruposComProduto[n - 1]; if (g) setGrupoSelId(g.id); }
    };
    const tecla = e => {
      if (e.key === 'Escape') {
        e.preventDefault(); e.stopPropagation();
        if (manual) { setManual(null); setPesoDigitado(''); return; }
        if (atalho) { setAtalho(''); return; }
        if (grupoSelId) { setGrupoSelId(null); return; }
        onFechar();
        return;
      }
      if (manual || lendo || e.ctrlKey || e.altKey || e.metaKey) return;
      if (/^[0-9]$/.test(e.key) && !busca) {
        e.preventDefault(); e.stopPropagation();
        const novo = atalho + e.key;
        const n = Number(novo);
        if (n < 1 || n > totalAtalhos) { setAtalho(''); return; }
        if (n * 10 > totalAtalhos) escolher(n); else setAtalho(novo);
        return;
      }
      if (atalho && e.key === 'Enter') { e.preventDefault(); e.stopPropagation(); escolher(Number(atalho)); return; }
      if (atalho && e.key === 'Backspace') { e.preventDefault(); e.stopPropagation(); setAtalho(a => a.slice(0, -1)); }
    };
    window.addEventListener('keydown', tecla, true);
    return () => window.removeEventListener('keydown', tecla, true);
  });

  if (!aberto) return null;

  // Janela no meio da tela (~56% x 88%): o PDV continua visível em volta.
  return (
    <div role="dialog" aria-label="Pesados"
      style={{ position: 'fixed', inset: 0, zIndex: 2000, background: 'rgba(15,23,42,0.35)' }}
      onClick={e => { if (e.target === e.currentTarget && !manual) onFechar(); }}>
      <div className={s.central} style={{
        position: 'absolute', top: '5vh', left: '50%', transform: 'translateX(-50%)',
        width: 'min(1080px, 56vw)', minWidth: 560, height: '88vh',
        background: '#f3f4f6', borderRadius: 10, boxShadow: '0 12px 40px rgba(0,0,0,0.35)',
        fontFamily: "'Segoe UI', Tahoma, sans-serif", fontSize: 13, color: '#1f2937',
      }}>
        <div className={s.buscaBar}>
          <input ref={buscaRef} className={s.buscaInput} placeholder="Buscar produto..."
            value={busca} onChange={e => setBusca(e.target.value)} />
          <button type="button"
            className={`${s.btnBalanca} ${balancaAtiva ? s.btnBalancaAtiva : s.btnBalancaInativa}`}
            onClick={onToggleBalanca}
            title={balancaAtiva ? 'Balança ligada — clique pra desligar e digitar o peso' : 'Balança desligada — clique pra capturar o peso da balança'}>
            ⚖️ Balança: {balancaAtiva ? 'Ligada' : 'Desligada'}
          </button>
          <button type="button" className={s.btnFechar} onClick={onFechar}>✕ Sair (Esc)</button>
        </div>
        <div className={s.categorias}>
          <button type="button" className={`${s.categoriaBtn} ${!grupoSelId ? s.categoriaBtnAtiva : ''}`}
            onClick={() => setGrupoSelId(null)}>Todos</button>
          {gruposComProduto.map((g, i) => (
            <button key={g.id} type="button" className={`${s.categoriaBtn} ${grupoSelId === g.id ? s.categoriaBtnAtiva : ''}`}
              onClick={() => setGrupoSelId(g.id)} title={`Atalho: ${i + 1}`}>
              {!numerarItens && <span data-atalho style={seloGrupo}>{i + 1}</span>}{g.nome}
            </button>
          ))}
        </div>

        {(atalho || lendo || erro || lancados.length > 0) && (
          <div style={{ padding: '8px 16px', background: '#fff', borderBottom: '1px solid #e5e7eb', fontSize: 13, display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            {atalho && (
              <span style={{ color: '#1565FF', fontWeight: 700 }}>
                Atalho: {atalho} — digite o próximo número ou Enter
              </span>
            )}
            {lendo && <span style={{ color: '#1565FF', fontWeight: 700 }}>⚖️ Lendo peso da balança…</span>}
            {erro && <span style={{ color: '#c62828', fontWeight: 600 }}>{erro}</span>}
            {!lendo && !erro && lancados[0] && (
              <span style={{ color: '#047857', fontWeight: 700 }}>
                ✔ {fmtPeso(lancados[0].peso)} {lancados[0].un} de {lancados[0].nome} — {fmtBRL(lancados[0].total)} lançado no PDV
              </span>
            )}
            {lancados.length > 1 && <span style={{ color: '#666' }}>{lancados.length} itens lançados nesta pesagem</span>}
          </div>
        )}

        <div className={s.produtosWrap}>
          {carregando ? (
            <div className={s.produtosVazio}>Carregando…</div>
          ) : filtrados.length === 0 ? (
            <div className={s.produtosVazio}>
              {produtos.length ? 'Nenhum produto encontrado.' : 'Nenhum produto marcado para esta tela — confira o atributo escolhido em Parâmetros → PDV → Botão Pesados.'}
            </div>
          ) : (
            <div className={s.produtosGrade} style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(104px, 1fr))', gap: 8 }}>
              {filtrados.map((p, i) => (
                <div key={p.id} className={s.produtoCard} onClick={() => clicarProduto(p)}
                  style={{ padding: 6, gap: 3, position: 'relative', ...(lendo ? { opacity: 0.6, cursor: 'wait' } : {}) }}>
                  {numerarItens && <span data-atalho style={seloItem}>{i + 1}</span>}
                  {p.foto_url ? <img className={s.produtoFoto} src={p.foto_url} alt={p.nome} />
                    : <span className={s.produtoFotoVazia}>📦</span>}
                  <span className={s.produtoNome}>{p.nome}</span>
                  <span className={s.produtoPreco}>{fmtBRL(precoDoProduto(p))}{p.unidade ? ` / ${p.unidade}` : ''}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {manual && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 2100, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={() => { setManual(null); setPesoDigitado(''); }}>
          <div style={{ background: '#fff', borderRadius: 10, padding: 20, width: 360, boxShadow: '0 8px 30px rgba(0,0,0,0.25)' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 4 }}>{manual.produto.nome}</div>
            <div style={{ color: '#555', fontSize: 13, marginBottom: 10 }}>
              {fmtBRL(precoDoProduto(manual.produto))}{manual.produto.unidade ? ` / ${manual.produto.unidade}` : ''}
            </div>
            {manual.aviso && <div style={{ color: '#b45309', fontSize: 13, fontWeight: 600, marginBottom: 8 }} title={manual.detalhe}>⚠️ {manual.aviso}</div>}
            <label style={{ fontSize: 12.5, fontWeight: 600 }}>Peso ({manual.produto.unidade || 'KG'})</label>
            <input ref={pesoRef} inputMode="decimal" aria-label="Peso"
              style={{ width: '100%', fontSize: 22, padding: '8px 10px', marginTop: 4, border: '1px solid #cbd5e1', borderRadius: 6, textAlign: 'right' }}
              placeholder="0,000" value={pesoDigitado}
              onChange={e => setPesoDigitado(e.target.value.replace(/[^\d.,]/g, ''))}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); confirmarManual(); } }} />
            {pesoDigitado && lerNumero(pesoDigitado) > 0 && (
              <div style={{ textAlign: 'right', marginTop: 6, fontSize: 13, color: '#047857', fontWeight: 700 }}>
                Total {fmtBRL(lerNumero(pesoDigitado) * precoDoProduto(manual.produto))}
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14 }}>
              <button type="button" className={s.btnFechar} onClick={() => { setManual(null); setPesoDigitado(''); }}>Cancelar (Esc)</button>
              <button type="button" className={s.btnFechar} style={{ background: '#1565FF', color: '#fff', borderColor: '#1565FF' }}
                onClick={confirmarManual}>Lançar (Enter)</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
